import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  Booking,
  BookingCancelReason,
  BookingCancelReasonsService,
  BookingService,
} from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Used by both parent and caregiver flows. `isSitter` flips the status code
// between sitter_cancel and parent_cancel.
const MESSAGE_MAX = 500;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking & { typeId?: number };
  isSitter: boolean;
  onCancelled?: () => void;
}

export const CancelBookingModal = ({
  isOpen,
  onClose,
  booking,
  isSitter,
  onCancelled,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState<BookingCancelReason[]>([]);
  const [reasonId, setReasonId] = useState<number | ''>('');
  const [reasonNote, setReasonNote] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setReasonId('');
    setReasonNote('');
    setMessage('');
    setError('');
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingCancelReasonsService.getByFilter({
          params: {
            bookingTypeId: booking.typeId,
            bookingType: booking.type,
            sitter: isSitter,
          },
        });
        setReasons((resp.data?.data ?? []) as BookingCancelReason[]);
      } catch {
        setReasons([]);
      }
    };
    void load();
  }, [isOpen, booking.typeId, booking.type, isSitter]);

  const selectedReason = reasons.find((r) => r.id === reasonId);

  const selectReason = (id: number): void => {
    setReasonId(id);
    const r = reasons.find((x) => x.id === id);
    if (r?.description) setMessage(r.description);
  };

  const otherRole = isSitter
    ? t('components.modals.cancelBooking.parent')
    : t('components.modals.cancelBooking.sitter');

  const submit = async (): Promise<void> => {
    if (!selectedReason) {
      setError(t('components.modals.cancelBooking.reasonRequired'));
      return;
    }
    if (selectedReason.type === 'other' && !reasonNote) {
      setError(t('components.modals.cancelBooking.reasonOtherRequired'));
      return;
    }
    if (!message) {
      setError(t('components.modals.cancelBooking.messageRequired', { role: otherRole }));
      return;
    }
    if (!booking.id) return;
    setSubmitting(true);
    setError('');
    try {
      await BookingService.patch(
        booking.id,
        {
          id: booking.id,
          rejectedReason: selectedReason.type,
          rejectedReasonNote:
            selectedReason.type === 'other' ? reasonNote : undefined,
          message,
          status: {
            code: isSitter ? 'sitter_cancel' : 'parent_cancel',
          },
        } as unknown as Booking,
      );
      toast.success(t('components.modals.cancelBooking.cancelled'));
      onCancelled?.();
      onClose();
    } catch {
      setError(t('components.modals.cancelBooking.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = MESSAGE_MAX - message.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('components.modals.cancelBooking.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('components.modals.cancelBooking.keep')}
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            {t('components.modals.cancelBooking.submit')}
          </Button>
        </>
      }
    >
      <label htmlFor='cancel-reason' className='block font-semibold mb-1'>
        {t('components.modals.cancelBooking.reasonLabel')}
      </label>
      <select
        id='cancel-reason'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
        value={reasonId}
        onChange={(e): void =>
          selectReason(e.target.value ? Number(e.target.value) : 0)
        }
      >
        <option value=''>{t('components.modals.cancelBooking.reasonPrompt')}</option>
        {reasons.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </select>

      {selectedReason?.type === 'other' && (
        <div className='mb-3'>
          <label htmlFor='cancel-reason-note' className='block font-semibold mb-1'>
            {t('components.modals.cancelBooking.reasonOtherLabel')}
          </label>
          <input
            id='cancel-reason-note'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={reasonNote}
            onChange={(e): void => setReasonNote(e.target.value)}
          />
        </div>
      )}

      <label htmlFor='cancel-message' className='block font-semibold mb-1'>
        {t('components.modals.cancelBooking.messageLabel', { role: otherRole })}
      </label>
      <div className='text-right italic text-xs text-neutral-500'>
        {t('components.modals.cancelBooking.charactersRemaining', { count: remaining })}
      </div>
      <textarea
        id='cancel-message'
        className='form-control w-full border border-neutral-300 rounded p-2 mb-3'
        rows={4}
        maxLength={MESSAGE_MAX}
        value={message}
        onChange={(e): void => setMessage(e.target.value)}
      />

      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};
